import * as React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  Layers3,
  Mail,
  ClipboardList,
  ScrollText,
  Receipt,
  ServerCog,
  Users,
  MoreHorizontal,
  LogOut,
  LifeBuoy,
} from "lucide-react";
import {
  EduViaOSMark,
  Sidebar,
  SidebarHeader,
  SidebarBrand,
  SidebarBody,
  SidebarGroup,
  SidebarItem,
  SidebarFooter,
  SidebarUser,
  Topbar,
  SidebarToggle,
  TopbarSearch,
  TopbarSpacer,
  TopbarActions,
  ThemeCustomizer,
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  MobileBottomNav,
  MobileBottomNavItem,
  MobileBottomNavMore,
  MobileBottomNavSheetItem,
  MobileBottomNavSheetClose,
  useAppearance,
  cn,
} from "@eduviaos/ui-kit";
import { useAuth } from "@/auth/AuthProvider";

interface NavEntry {
  to: string;
  label: string;
  icon: React.ReactNode;
}

const PLATFORM_NAV: NavEntry[] = [
  { to: "/dashboard", label: "Overview", icon: <LayoutDashboard /> },
  { to: "/tenants", label: "Tenants", icon: <Building2 /> },
  { to: "/verticals", label: "Verticals", icon: <Layers3 /> },
  { to: "/capabilities", label: "Capabilities", icon: <ServerCog /> },
  // Phase B2.2: SA-level "Forms" entry removed — every form is
  // tenant-scoped. Forms are edited via the tenant detail page
  // (Forms tab → "Edit fields" on each cloned form).
];

const OPS_NAV: NavEntry[] = [
  { to: "/support", label: "Centre support", icon: <LifeBuoy /> },
  { to: "/audit", label: "Audit log", icon: <ScrollText /> },
  { to: "/billing", label: "Billing", icon: <Receipt /> },
  { to: "/users", label: "Platform users", icon: <Users /> },
];
const SETTINGS_NAV: NavEntry[] = [
  { to: "/settings/email-templates", label: "Email templates", icon: <Mail /> },
  {
    to: "/settings/assessment-templates",
    label: "Assessment templates",
    icon: <ClipboardList />,
  },
];

function NavSidebarItem({ to, label, icon }: NavEntry) {
  return (
    <NavLink to={to} end className="contents">
      {({ isActive }) => <SidebarItem icon={icon} label={label} active={isActive} />}
    </NavLink>
  );
}

/**
 * Bottom-nav split for super-admin. The four routes that account for
 * 80%+ of platform-ops daily taps live in the bar; the rest go in the
 * "More" sheet. Order: Overview → Tenants → Capabilities → Audit.
 */
const MOBILE_PRIMARY: NavEntry[] = [
  PLATFORM_NAV[0]!, // Overview
  PLATFORM_NAV[1]!, // Tenants
  PLATFORM_NAV[3]!, // Capabilities
  OPS_NAV[0]!, // Centre support
];
const MOBILE_OVERFLOW: NavEntry[] = [
  PLATFORM_NAV[2]!, // Verticals
  OPS_NAV[1]!, // Audit log
  OPS_NAV[2]!, // Billing
  OPS_NAV[3]!, // Platform users
];

function NavBottomItem({ to, label, icon }: NavEntry) {
  return (
    <NavLink to={to} end className="contents">
      {({ isActive }) => <MobileBottomNavItem icon={icon} label={label} active={isActive} />}
    </NavLink>
  );
}

function NavBottomSheetItem({ to, label, icon }: NavEntry) {
  return (
    <NavLink to={to} end className="contents">
      {({ isActive }) => (
        <MobileBottomNavSheetClose asChild>
          <MobileBottomNavSheetItem icon={icon} label={label} active={isActive} />
        </MobileBottomNavSheetClose>
      )}
    </NavLink>
  );
}

export function AppShell() {
  const { user, logout } = useAuth();
  const { layout } = useAppearance();
  const navigate = useNavigate();

  const onSignOut = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const initials =
    (user?.email ?? "??")
      .split("@")[0]!
      .split(/[._-]/)
      .map((s) => s[0]?.toUpperCase() ?? "")
      .join("")
      .slice(0, 2) || "?";

  return (
    // Phase B2.5 / Iron Rule #16 follow-up: the app frame is fixed at
    // viewport height so child pages can opt into a flex chain that
    // makes the DataTable fill the remaining space (and ONLY the
    // table scrolls). Pages that don't opt in (most do not) still
    // get natural page-level scroll because <main> is overflow-y-auto.
    <div className="flex h-dvh">
      <Sidebar>
        <SidebarHeader>
          <SidebarBrand
            icon={<EduViaOSMark className="size-6" />}
            tile="neutral"
            title="EduViaOS"
            subtitle="Super Admin"
          />
        </SidebarHeader>
        <SidebarBody>
          <SidebarGroup label="Platform">
            {PLATFORM_NAV.map((entry) => (
              <NavSidebarItem key={entry.to} {...entry} />
            ))}
          </SidebarGroup>
          <SidebarGroup label="Settings">
            {SETTINGS_NAV.map((entry) => (
              <NavSidebarItem key={entry.to} {...entry} />
            ))}
          </SidebarGroup>
          <SidebarGroup label="Operations">
            {OPS_NAV.map((entry) => (
              <NavSidebarItem key={entry.to} {...entry} />
            ))}
          </SidebarGroup>
        </SidebarBody>
        <SidebarFooter>
          <SidebarUser
            name={user?.full_name ?? user?.email?.split("@")[0] ?? "User"}
            email={user?.email ?? ""}
            initials={initials}
            menuTrigger={
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Account menu">
                    <MoreHorizontal aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" side="top">
                  <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={onSignOut}>
                    <LogOut className="size-4" aria-hidden="true" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            }
          />
        </SidebarFooter>
      </Sidebar>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <Topbar>
          <span className="hidden md:contents">
            <SidebarToggle />
          </span>
          <TopbarSearch placeholder="Search tenants, capabilities, audit…" />
          <TopbarSpacer />
          <TopbarActions>
            <ThemeCustomizer />
          </TopbarActions>
        </Topbar>

        <main
          className={cn(
            // `min-h-0 overflow-y-auto` together: the flex parent
            // bounds main to the remaining viewport, and overflow-y
            // auto means main scrolls when its content overflows.
            // Pages that pass `h-full flex flex-col` claim the full
            // height and manage scroll themselves (so the table can
            // be the only scroll surface).
            //
            // No top padding here — the canonical 12px breathing
            // room is owned by <PageHeader>'s own pt-3 (Phase B2.6
            // polish). Pages without <PageHeader> add pt-3 to their
            // own root div. This keeps the gap rock-solid while
            // scrolling instead of collapsing to 0 once a sticky
            // header docks (the previous behaviour read as the
            // breadcrumb "scrolling under" the Topbar).
            "min-h-0 flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8",
            "pb-[calc(80px+env(safe-area-inset-bottom))] md:pb-8",
            layout === "centered" ? "mx-auto w-full max-w-6xl" : "w-full",
          )}
        >
          <Outlet />
        </main>
      </div>

      <MobileBottomNav>
        {MOBILE_PRIMARY.map((entry) => (
          <NavBottomItem key={entry.to} {...entry} />
        ))}
        <MobileBottomNavMore
          sheetTitle="Navigation"
          sheetDescription="Tap an item to navigate"
          header={
            <div className="border-border bg-muted/40 flex items-center gap-2.5 rounded-lg border px-3 py-2.5">
              <span className="bg-card border-border/60 grid size-8 shrink-0 place-items-center rounded-md border">
                <EduViaOSMark className="size-6" />
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold leading-tight">EduViaOS</p>
                <p className="text-muted-foreground truncate text-[11px] leading-tight">
                  Super Admin
                </p>
              </div>
            </div>
          }
          footer={
            <div className="flex items-center gap-2.5">
              <div
                aria-hidden="true"
                className="bg-primary/15 text-primary grid size-9 shrink-0 place-items-center rounded-full text-xs font-semibold"
              >
                {initials}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium leading-tight">
                  {user?.full_name ?? user?.email?.split("@")[0] ?? "User"}
                </p>
                <p className="text-muted-foreground truncate text-xs leading-tight">
                  {user?.email}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onSignOut}
                aria-label="Sign out"
                className="shrink-0"
              >
                <LogOut className="size-4" aria-hidden="true" />
                Sign out
              </Button>
            </div>
          }
        >
          {MOBILE_OVERFLOW.map((entry) => (
            <NavBottomSheetItem key={entry.to} {...entry} />
          ))}
        </MobileBottomNavMore>
      </MobileBottomNav>
    </div>
  );
}
